// Display helpers for the Storefront page. Server values stay codes and numbers; words come
// from the `storefront` namespace and numbers from the app locale.
import { formatCopy } from '@/i18n/ns';
import type { StorefrontCopyKey } from '@/i18n/ns/storefront';
import { credits as creditsOf, formatters } from '@/usage/format';

/**
 * Credits as the company's plan counts them (D74), the only money customers see: whole, one decimal when small,
 * and "<0.1" for the fraction a single chat uses, so it never reads as nothing.
 */
export function creditNumber(micros: number | null | undefined, locale: string): string {
  const value = creditsOf(micros);
  const format = formatters(locale);
  return value > 0 && value < 0.1 ? `<${tenth(locale)}` : format.amount(value);
}

/** Whether an amount is the fraction of a credit `creditNumber` shows as "<0.1". */
export const underTenth = (micros: number | null | undefined) => {
  const value = creditsOf(micros);
  return value > 0 && value < 0.1;
};

/** A tenth of a credit in the locale's numbers ("0.1", "0,1"). */
export const tenth = (locale: string) => formatters(locale).one(0.1);

/** The same in running text, with its unit: "12 credits", "under 0.1 credits". */
export function creditsPhrase(
  micros: number | null | undefined,
  locale: string,
  copy: Readonly<Record<StorefrontCopyKey, string>>,
): string {
  const value = creditsOf(micros);
  const format = formatters(locale);
  const amount = format.amount(value);
  return value > 0 && value < 0.1
    ? formatCopy(copy.creditsUnder, { amount: tenth(locale) })
    : formatCopy(amount === format.amount(1) ? copy.creditsOne : copy.creditsAmount, { amount });
}

export function percent(part: number, whole: number, locale: string): string {
  if (!whole) return new Intl.NumberFormat(locale, { style: 'percent' }).format(0);
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(
    part / whole,
  );
}

export function signedPercent(now: number, before: number, locale: string): string | null {
  if (!before) return null;
  const change = (now - before) / before;
  const text = new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 0,
    signDisplay: 'exceptZero',
  }).format(change);
  return change > 0 ? `▲ ${text}` : change < 0 ? `▼ ${text}` : text;
}

/** "14:52" today, "Tue 14:52" this week, "26 Sept" before that; a 24-hour clock, as in Agents. */
export function when(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const time = date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  if (date.toDateString() === now.toDateString()) return time;
  const days = (now.getTime() - date.getTime()) / 86_400_000;
  if (days < 6) return `${date.toLocaleDateString(locale, { weekday: 'short' })} ${time}`;
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

export function weekday(iso: string, locale: string, narrow = false): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString(locale, { weekday: narrow ? 'narrow' : 'short' });
}

/** Whole minutes for a conversation's length; under a minute reads "<1". */
export function wholeMinutes(minutes: number): string {
  return minutes < 1 ? '<1' : String(Math.round(minutes));
}

export function minutesSince(iso: string | undefined | null): number {
  if (!iso) return 0;
  const start = new Date(iso).getTime();
  return Number.isNaN(start) ? 0 : Math.max(0, Math.round((Date.now() - start) / 60_000));
}
